/**
 * Loads Google Analytics and Microsoft Clarity only after the user has accepted cookies.
 * Listens for consent and injects scripts when consent is 'accepted'.
 * Does not load analytics scripts until then (recommended for compliance).
 *
 * Clarity is initialized after a short delay so the page (and styles) are fully
 * loaded before recording starts; GA loads immediately after consent.
 *
 * Clarity heatmaps: For heatmaps/recordings to show site styles, the CDN must allow
 * User-Agent "Clarity-Bot" for static assets, and stylesheet links must not use
 * integrity/SRI (see Microsoft's heatmap troubleshooting docs).
 */

import { getCookieConsent } from './cookie-banner.js';

let gaInited = false;
let clarityInited = false;

/** Delay in ms before initializing Clarity (allows page/styles to be ready for recording). */
const CLARITY_INIT_DELAY_MS = 2000;

const GA_ID_REGEX = /^(G|UA|GTM)-[A-Z0-9-]+$/i;
const CLARITY_ID_REGEX = /^[a-z0-9]+$/i;

function initGA() {
  if (gaInited) {
    return;
  }
  const meta = document.querySelector('meta[name="ga-tracking-id"]');
  const trackingId = meta?.content?.trim();
  if (!trackingId || !GA_ID_REGEX.test(trackingId)) {
    return;
  }
  gaInited = true;
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = window.gtag || gtag;

  const script = document.createElement('script');
  script.async = true;

  if (/^GTM-/i.test(trackingId)) {
    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });
    script.src = `https://www.googletagmanager.com/gtm.js?id=${trackingId}`;
  } else {
    gtag('js', new Date());
    gtag('config', trackingId);
    script.src = `https://www.googletagmanager.com/gtag/js?id=${trackingId}`;
  }

  const firstScript = document.querySelector('script');
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  } else {
    document.head.appendChild(script);
  }
}

function initClarity() {
  if (clarityInited) {
    return;
  }
  const meta = document.querySelector('meta[name="clarity-id"]');
  const clarityId = meta?.content?.trim();
  if (!clarityId || !CLARITY_ID_REGEX.test(clarityId)) {
    return;
  }
  clarityInited = true;
  window.clarity =
    window.clarity ||
    function () {
      (window.clarity.q = window.clarity.q || []).push(arguments);
    };
  const clarityScript = document.createElement('script');
  clarityScript.async = true;
  clarityScript.src = `https://www.clarity.ms/tag/${clarityId}`;
  const firstScript = document.querySelector('script');
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(clarityScript, firstScript);
  } else {
    document.head.appendChild(clarityScript);
  }
}

function tryLoadAnalytics() {
  if (getCookieConsent() !== 'accepted') {
    return;
  }
  initGA();
  // Delay Clarity so the page and styles are fully loaded before recording starts
  window.setTimeout(() => {
    initClarity();
  }, CLARITY_INIT_DELAY_MS);
}

/**
 * Call once (e.g. on window load). Sets up consent check and listener so GA/Clarity
 * load only when the user has accepted cookies.
 */
export function initAnalyticsLoader() {
  tryLoadAnalytics();
  window.addEventListener('ally:cookieConsent', (event) => {
    if (event.detail?.consent === 'accepted') {
      tryLoadAnalytics();
    }
  });
}
